import Button from '@/components/button';
import './_jump-to-section.scss';
import { renderToString } from 'react-dom/server';
import { useContext, useEffect, useState } from 'react';
import { LayoutContext } from '@/components/layout-context-container';

// Helper function to extract text content from label (strips HTML tags)
const getTextFromLabel = (label) => {
  const htmlString = renderToString(label);
  return htmlString.replace(/<[^>]*>/g, '');
};

function PatientJumpToSection({ links, maxWidth = 800, isSticky = false }) {

  /**
   * @typedef {Object} PatientJumpToSectionProps
   * @param {string} link The URL fragment to jump to.
   * @param {import('react').ReactNode} label The ReactNode to display for the link.
   * @param {Object} [rest] Additional properties to pass to the Button component.
   *
   * @type {PatientJumpToSectionProps[] | null}
   */
  const [linkData, setLinkData] = useState(null);
  const [jumpTo, setJumpTo] = useState(null);
  const [maxWidthData, setMaxWidthData] = useState(maxWidth);
  const [hide, setHide] = useState(isSticky);
  const { jumpSectionData, dispatchJumpSectionData, cookieConsent, setAutoScrollActive, autoScrollActive } = useContext(LayoutContext);

  useEffect(() => {
    if (!isSticky) {
      setLinkData(links);
      setMaxWidthData(maxWidth);
      dispatchJumpSectionData({ type: 'SET_DATA', links, maxWidth });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSticky, links]);

  useEffect(() => {
    if (isSticky) {
      jumpSectionData?.links && setLinkData(jumpSectionData.links);
      jumpSectionData?.maxWidth && setMaxWidthData(jumpSectionData.maxWidth);
    }
  }, [isSticky, jumpSectionData]);

  useEffect(() => {
    // reset jump section data on unmount
    return () => {
      !isSticky && dispatchJumpSectionData({ type: 'SET_RESET' });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const showCookieConsent = cookieConsent() !== 'Consent';

    function handle() {
      if (isSticky) {
        const jumpSection = document.querySelector('.patient-jump-to-section:not(.patient-jump-to-section--sticky)');
        // allow show/hide of the jump to section based on auto scroll activity
        if (jumpSection && !autoScrollActive) {
          const rect = jumpSection.getBoundingClientRect();
          // hardcoded 38px which is the cookie policy height
          if ((rect.top < 38 && showCookieConsent) || (rect.top < 0 && !showCookieConsent)) {
            setHide(false);
          } else {
            setHide(true);
          }

        } else {
          setHide(true);
        }
      }
    }

    isSticky && handle();

    window.addEventListener('resize', handle);
    document.addEventListener('scroll', handle);

    return () => {
      window.removeEventListener('resize', handle);
      document.removeEventListener('scroll', handle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSticky, autoScrollActive]);

  useEffect(() => {
    // Create observer to detect when we've reached the target position
    const checkScrollPosition = () => {
      const y = document.getElementById(jumpTo)?.getBoundingClientRect().top + window.scrollY;
      const currentY = window.scrollY;
      if (jumpTo && Math.abs(currentY - y) < 10) {
        // We've reached the target position (within 10px tolerance)
        window.removeEventListener('scroll', checkScrollPosition);
        setTimeout(() => {
          setAutoScrollActive(false);
          setJumpTo(null);
          // console.log('Reached target position');
        }, 100); // Allow some time for the scroll to settle
        return;
      }
    };

    if (autoScrollActive && jumpTo) {
      window.addEventListener('scroll', checkScrollPosition);

      const offset = document.querySelector(`.patient-jump-to-section__link[data-link="${jumpTo}"]`)?.getAttribute('data-offset') || '0';

      const y = document.getElementById(jumpTo)?.getBoundingClientRect().top + window.scrollY + parseInt(offset, 10);
      window.scrollTo({top: y, behavior: "smooth"});
      
      // Cleanup listener after 2 seconds as fallback
      setTimeout(() => {
        setAutoScrollActive(false);
        setJumpTo(null);
        // console.log('Auto scroll timeout reached, stopping');
        window.removeEventListener('scroll', checkScrollPosition);
      }, 2000);
    }

    return () => {
      window.removeEventListener('scroll', checkScrollPosition);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoScrollActive, jumpTo])

  function handleClick(e) {
    // overriding default behavior due to poor native performance of scrolling to anchor links
    e.preventDefault();
    e.stopPropagation();
    const jumpLink = e.currentTarget.getAttribute('data-link');
    setJumpTo(jumpLink);
    setAutoScrollActive(true);
  }

  return (
    <>
      {jumpSectionData?.links && <section className={`patient-jump-to-section bottom-divider bottom-divider--shadow ${isSticky ? 'patient-jump-to-section--sticky' : ''}`.trim()}>
        <div className={`container container--px-0 ${hide ? 'patient-jump-to-section--hidden' : ''}`.trim()} style={{ maxWidth: `${maxWidthData}px`, display: hide ? 'none' : 'block' }}>
          <div className="patient-jump-to-section__title">Jump to section:</div>
          <ul className="patient-jump-to-section__content">
            {linkData && linkData?.map(({ link, label, offset = undefined, ...rest }, index) => (
              <li key={index}>
                <Button
                  key={index}
                  buttonUrl={`#${link}`}
                  className="patient-jump-to-section__link"
                  useInline
                  data-gtm-event-name="Anchor Link"
                  data-gtm-event-category="Navigation"
                  data-gtm-event-action="Click"
                  data-gtm-event-label={getTextFromLabel(label)}
                  target="_self"
                  data-link={link}
                  data-offset={offset}
                  onClick={handleClick}
                  {...rest}
                >
                  {label}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      </section>}
    </>
  );
}

export default PatientJumpToSection;